import FlexBox from '@components/layout/FlexBox';
import "./Calendar.css";
import { 
  CALENDARS, 
  parseDate, 
  getDaysInMonth, 
  composeDate, 
  getWeekday,
  findHolidaysForMonth,
  findSeasonsForMonth,
  getHolidayRange,
  dateString,
  mmddString,
  mmddRangeString,
  getMMDDFromYearDay,
  getSeasonRange, 
} from '@dndModels/Calendars';
import React, { useMemo } from 'react';
import StringBox from '../strings/StringBox';
import SelectStringBox from '../selects/SelectStringBox';
import { DisablableOption } from '@models';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import CommonUtilities from '@utilities/common_utilities';

interface Props {
  name: string;
  value: number;
  today?: number;
  onChange: (changed: number) => void;
  calendar_id: string;
}

const common_util = CommonUtilities.getInstance();

export default function CalendarBox({
  name,
  value,
  today,
  onChange,
  calendar_id
}: Props) {
  const calendar = CALENDARS.find(o => o.true_id === calendar_id);

  if (!calendar) {
    // No calendar selected - fall back to a plain numeric input.
    return (
      <input
        id={name}
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      />
    );
  }
  
  const { year, month, day } = parseDate(value);

  const sortedMonths = useMemo(
    () => ([...calendar.months].sort((a, b) => a.position - b.position)),
    [calendar]
  );
  const month_options = sortedMonths.map(o => {
    const month: DisablableOption = {
      id: `${o.position}`, text: o.name.length > 0 ? o.name : "Nameless"
    }
    return month;
  });

  const currentMonth = sortedMonths.find(m => m.position === month) ?? sortedMonths[0];
  
  const holidays = useMemo(
    () => {
      const max_month = Math.max(...calendar.months.map(o => o.position));
      if (month === 1) {
        return [...findHolidaysForMonth(calendar, max_month),...findHolidaysForMonth(calendar, month),...findHolidaysForMonth(calendar, month + 1)];
      } else if (month === max_month) {
        return [...findHolidaysForMonth(calendar, month - 1),...findHolidaysForMonth(calendar, month),...findHolidaysForMonth(calendar, 1)];
      } else {
        return [...findHolidaysForMonth(calendar, month - 1),...findHolidaysForMonth(calendar, month),...findHolidaysForMonth(calendar, month + 1)];
      }
    },
    [calendar, month]
  );

  const seasons = useMemo(
    () => ([...findSeasonsForMonth(calendar, month)]),
    [calendar, month]
  );

  const week_length = calendar.weekdays.length;

  const handleDateChange = ({ newYear, newMonth, newDay }: { newYear?: number, newMonth?: number, newDay?: number }) => {
    const use_year = newYear === undefined ? year : newYear;
    const use_month = newMonth === undefined ? month : newMonth;
    const use_day = newDay === undefined ? day : newDay;
    const monthObj = sortedMonths.find(m => m.position === use_month) ?? sortedMonths[0];
    const maxDay = getDaysInMonth(calendar, monthObj, use_year);
    onChange(composeDate(use_year, use_month, Math.min(use_day, maxDay)));
  };

  const day_width = 100 / week_length;
  const day_width_style = { width: `${day_width}%` };

  const renderWeekDayHeader = (this_day: number) => {
    const weekday = calendar.weekdays.find(o => o.position === this_day);
    return (
      <FlexBox xs_expand={1} key={this_day} direction="column" 
        className="calendar-weekday-header centered" style={day_width_style}>
        { weekday ? weekday.name.substring(0, 3) : "" }
      </FlexBox>
    );
  }

  const renderDay = (this_day: number) => {
    const mmdd = month * 100 + this_day;
    const my_holidays = holidays.filter(o => common_util.numInRange({ range: getHolidayRange(calendar, o), num: mmdd, resets: true }));
    const season_endpoints = seasons.filter(o => o.mmdd_start === mmdd || o.mmdd_end === mmdd);
    let className = "calendar-day";
    if (day === this_day) {
      className += " selected";
    } else {
      className += " clickable";
    }
    if (today) {
      const parsed_today = parseDate(today);
      if (parsed_today.year === year && parsed_today.month === month && parsed_today.day === this_day) {
        className += " today";
      }
    }
    if (my_holidays.length > 0 || season_endpoints.length > 0) {
      className += " holiday";
    }
    let season_string = "";
    if (season_endpoints.length > 0)  {
      for (const season of season_endpoints) {
        if (season.mmdd_end === mmdd) {
          season_string += "Last Day of " + season.name;
        } else if (season.mmdd_start === mmdd) {
          season_string += "First Day of " + season.name;
        }
      }
    } else {
      // debugger;
      const my_seasons = seasons.filter(o => common_util.numInRange({ range: getSeasonRange(o), num: mmdd, resets: true }));
      season_string += `Season${ my_seasons.length > 1 ? "s" : "" }: ${ my_seasons.map(o => `${o.name} (${mmddRangeString({ mmdd_start: o.mmdd_start, mmdd_end: o.mmdd_end, calendar })})`).join(", ")}`;
    }
    return (
      <FlexBox xs_expand={1} key={this_day} direction="column" 
        className={className} style={day_width_style}
        // log_it={this_day === 8}
        tooltip={
          <FlexBox className="calendar-tooltip">
            <FlexBox>
              { mmddString({ mmdd, calendar }) }
            </FlexBox>
            <FlexBox xs_expand={0}>{ season_string }</FlexBox>
            { my_holidays.length > 0 &&
              <FlexBox xs_expand={0}>Holiday{ my_holidays.length > 1 ? "s" : "" }: { my_holidays.map(o => {
                if (o.length && o.length > 1) {
                  const use_mmdd = o.mmdd ? o.mmdd : (o.year_day ? getMMDDFromYearDay({ calendar, year_day: o.year_day }) : -1);
                  return `${o.name} (${mmddRangeString({ mmdd_start: use_mmdd, length: o.length, calendar })})`;
                }
                return o.name;
              }).join(", ") }</FlexBox>
            }
          </FlexBox>
        }
        onClick={() => {
          if (day !== this_day) {
            handleDateChange({ newDay: this_day });
          }
        }}>
        <FlexBox><div style={{ float: "right" }}>{this_day}</div></FlexBox>
      </FlexBox>
    );
  }

  const renderOtherMonthDay = (this_month: number, this_day: number) => {
    let className = "calendar-day other-month clickable";
    if (today) {
      const parsed_today = parseDate(today);
      if (parsed_today.year === year && parsed_today.month === this_month && parsed_today.day === this_day) {
        className += " today";
      }
    }
    const mmdd = this_month * 100 + this_day;
    const my_holidays = holidays.filter(o => common_util.numInRange({ range: getHolidayRange(calendar, o), num: mmdd, resets: true }));
    const season_endpoints = seasons.filter(o => o.mmdd_start === mmdd || o.mmdd_end === mmdd);
    if (my_holidays.length > 0 || season_endpoints.length > 0) {
      className += " holiday";
    }
    return (
      <FlexBox xs_expand={1} key={`${this_month}_${this_day}`} direction="column" 
        className={className} style={day_width_style}
        onClick={() => {
          handleDateChange({ newMonth: this_month, newDay: this_day});
        }}>
        <FlexBox><div style={{ float: "right" }}>{this_day}</div></FlexBox>
      </FlexBox>
    );
  }

  const renderEmptyDay = (this_day: number) => {
    let className = "calendar-day empty";
    return (
      <FlexBox xs_expand={1} key={`empty_${this_day}`} direction="column" 
        className={className} style={day_width_style}>
        <FlexBox><div style={{ float: "right" }}></div></FlexBox>
      </FlexBox>
    );
  }

  const renderStandardWeek = (first_day: number) => {
    const rendered_days: any[] = [];
    for (let i = 0; i < week_length; ++i) {
      rendered_days.push(renderDay(first_day + i));
    }
    return rendered_days;
  }

  const renderMonthDays = () => {
    if (currentMonth) {
      if (currentMonth.no_weekdays) {
        const rendered_days: any[] = [];
        for (let i = 1; i <= currentMonth.days; ++i) {
          rendered_days.push(renderDay(i));
        }
        return (
          <FlexBox direction="row">
            { rendered_days }
          </FlexBox>
        );
      } else {
        const first_wkday = getWeekday(calendar_id, composeDate(year, month, 1));
        const rendered_weeks: any[] = [];
        rendered_weeks.push(
          <FlexBox key="weekday_headers" direction="row">
            { calendar.weekdays.map(o => renderWeekDayHeader(o.position)) }
          </FlexBox>
        );
        if (first_wkday) {
          let this_day = 1;
          while (this_day <= currentMonth.days) {
            const rendered_days: any[] = [];
            if (this_day === 1) {
              if (first_wkday.position > 1) {
                // Figure out how many days from the previous month are part of the same week.
                const days_from_prev_month = first_wkday.position - 1;
                const prev_month = currentMonth.position === 1 ? calendar.months.find(o => o.position === Math.max(...calendar.months.map(o => o.position))) : calendar.months.find(o => o.position === currentMonth.position - 1);
                if (prev_month) {
                  const first_day_from_prev_month = prev_month.days - days_from_prev_month + 1;
                  for (let i = first_day_from_prev_month; i <= prev_month.days; ++i) {
                    rendered_days.push(renderOtherMonthDay(prev_month.position, i));
                  }
                }
                const remaining_days_of_week = week_length - days_from_prev_month;
                for (let i = 1; i <= remaining_days_of_week; ++i) {
                  rendered_days.push(renderDay(i));
                }
                this_day += week_length - first_wkday.position + 1;
              } else {
                rendered_days.push(...renderStandardWeek(1));
                this_day += week_length;
              }
            } else if (this_day + week_length - 1 > currentMonth.days) {
              while (this_day <= currentMonth.days) {
                rendered_days.push(renderDay(this_day));
                this_day++;
              }
              // debugger;
              const next_month = currentMonth.position === Math.max(...calendar.months.map(o => o.position)) ? calendar.months.find(o => o.position === 1) : calendar.months.find(o => o.position === currentMonth.position + 1);
              if (next_month && (!calendar.restart_weekdays_on_new_year || currentMonth.position !== Math.max(...calendar.months.map(o => o.position)))) {
                let next_month_day = 1;
                while (rendered_days.length < week_length) {
                  rendered_days.push(renderOtherMonthDay(next_month.position, next_month_day));
                  next_month_day++;
                }
              } else {
                let next_month_day = 1;
                while (rendered_days.length < week_length) {
                  rendered_days.push(renderEmptyDay(next_month_day));
                  next_month_day++;
                }
              }

              this_day += week_length; // Probably overkill, but it's fine.
            } else {
              rendered_days.push(...renderStandardWeek(this_day));
              this_day += week_length;
            }
            rendered_weeks.push(
              <FlexBox key={this_day} direction="row">
                { rendered_days }
              </FlexBox>
            );
          }
        }
        return rendered_weeks;
      }
    }
    return null;
  }

  const setToToday = () => {
    if (today !== undefined) {
      const parsed_today = parseDate(today);
      handleDateChange({ newDay: parsed_today.day, newMonth: parsed_today.month, newYear: parsed_today.year });
    }
  }

  // debugger;
  return (
    <FlexBox direction="column" className="calendar-box">
      <FlexBox className="header centered">{ name }</FlexBox>
      <FlexBox direction="row" xs_expand={0} style={{ backgroundColor: "black", color: "white" }}>
        { today !== undefined &&
          <FlexBox className="centered">
            <DandDButtonBox 
              name="Today"
              onClick={setToToday}
            />
          </FlexBox>
        }
        <FlexBox className="padded">
          <StringBox
            name="Year"
            type="number"
            value={year}
            onChange={(changed: string) => handleDateChange({ newYear: +changed }) }
          />
        </FlexBox>
        <FlexBox className="padded">
          <SelectStringBox
            name="Month"
            value={`${month}`}
            options={month_options}
            onChange={(changed: string) => handleDateChange({ newMonth: +changed })} 
          />
        </FlexBox>
      </FlexBox>
      { renderMonthDays() }
{/* 
      <div className="calendar-box-meta">
        {weekdayName && <span className="calendar-box-weekday">{weekdayName}</span>}
        {season && <span className="calendar-box-season">{season.name}</span>}
        {holidays && <span className="calendar-box-holiday">{holidays.map(o => o.name).join(", ")}</span>}
      </div> */}
    </FlexBox>
  );
}
